/**
 * OrgSelect page — lets the user choose the active organization context.
 *
 * @module apps/web/pages/OrgSelect
 * @since W2.9
 */

import { Loader2, Check, Shield, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout.js';
import { useAuth } from '../contexts/auth.js';
import { useOrgContext } from '../contexts/org.js';
import { ProtectedRoute } from '../components/ProtectedRoute.js';

function formatRole(role?: string): string {
  if (!role) {
    return 'Member';
  }

  return role.charAt(0).toUpperCase() + role.slice(1);
}

function roleIcon(role?: string) {
  if (role === 'owner') {
    return <Crown className="h-3.5 w-3.5 text-amber-300" />;
  }

  if (role === 'admin') {
    return <Shield className="h-3.5 w-3.5 text-sky-300" />;
  }

  return null;
}

function OrgSelectContent() {
  const { claims } = useAuth();
  const { memberships, selectedOrgId, setSelectedOrgId, loading } = useOrgContext();
  const navigate = useNavigate();

  function handleContinue(orgId: string) {
    setSelectedOrgId(orgId);
    navigate('/workspace', { replace: true });
  }

  return (
    <AuthLayout
      title="Select workspace"
      subtitle={claims?.email ? `Signed in as ${claims.email}` : 'Choose the organization context to use in Rule42 Router.'}
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : memberships.length > 0 ? (
          memberships.map((organization) => {
            const isSelected = organization.orgId === selectedOrgId;
            const icon = roleIcon(organization.role);

            return (
              <button
                key={organization.orgId}
                onClick={() => handleContinue(organization.orgId)}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-lg
                           bg-space-700 border border-slate-700 hover:border-indigo-500/60
                           hover:bg-space-700/80 transition-all duration-150 text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-brand-gradient flex items-center justify-center shrink-0">
                  <span className="text-white font-semibold text-sm">
                    {organization.orgName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-100 truncate">{organization.orgName}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                    {icon}
                    <span>{formatRole(organization.role)}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500 font-mono truncate">{organization.orgId}</p>
                </div>
                {isSelected && <Check className="h-4 w-4 shrink-0 text-emerald-300" />}
              </button>
            );
          })
        ) : (
          <div className="text-center">
            <p className="text-sm text-slate-400 mb-4">
              Your account isn&apos;t associated with any organisation.
              Contact your administrator to gain access.
            </p>
            <a href="/login" className="btn-secondary inline-flex">Back to login</a>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}

export function OrgSelectPage() {
  return (
    <ProtectedRoute>
      <OrgSelectContent />
    </ProtectedRoute>
  );
}
